import { useEffect, useMemo, useRef, useState } from "react";
import { applyPalette } from "@/lib/palette";
import { lei, chisinauTime } from "@/shared/format";
import { Suggestion } from "./Suggestion";
import { statusTone } from "@/shared/status-tone";
import { AddHomeMark, ShareMark } from "@/shared/install-marks";

type Item = {
  id: string;
  product_id: string;
  name: string;
  description: string | null;
  story: string | null;
  producer: string | null;
  price_minor: number;
  category_id: string;
  category_name: string | null;
  age_restricted: boolean;
  allergens: string[];
  available_from: number | null;
  available_until: number | null;
};

type Snapshot = {
  event: { id: string; slug: string; name: string; starts_at: number; ends_at: number; status: string };
  house: {
    name: string;
    locality: string | null;
    palette: string;
    logo: boolean;
    /** The house's motto. Absent on a pre-v4 snapshot, "" when unset. */
    footer_note?: string;
    /**
     * Who is legally selling: the registered company, its IDNO and its
     * registered address. Absent on a pre-v5 snapshot, and absent — not an
     * object of empty strings — for a house that has filled in none of it.
     */
    legal?: { name: string; idno: string; address: string };
  };
  rules: {
    payment_methods: string[];
    tips_enabled: boolean;
    tip_percents: number[];
    require_guest_name: boolean;
    closing_grace_hours: number;
    /** The house's terms page. Absent on a pre-v5 snapshot, "" when it has none. */
    terms_url?: string;
    /** Per-method required contact fields — absent pre-v8, defaults then. */
    checkout_contact?: Record<string, { email: boolean; phone: boolean }>;
  };
  lang: string;
  currency: string;
  /**
   * `photo` says whether a REAL photograph was uploaded. Nothing renders on it
   * any more — the picture is drawn either way, and /api/img/category/:id
   * decides what to serve — so it is informational, and the one thing that
   * could later tell a landscape photograph apart from a square drawing if the
   * two ever want different framing.
   */
  categories: { id: string; name: string; sort_order: number; photo?: boolean }[];
  /** code -> the house's own word for the place. Absent on an old snapshot. */
  zones?: { code: string; label: string }[];
  items: Item[];
};

const LANGS = [["ro", "RO"], ["ru", "RU"], ["en", "EN"]] as const;

const T = {
  ro: {
    loading: "Se încarcă meniul…", notFound: "Nu găsim meniul acestui eveniment.",
    notFoundHint: "Verifică dacă ai scanat codul de pe masă.",
    unpublished: "Meniul serii nu e publicat încă.",
    unpublishedHint: "Codul de pe masă e bun. Mai încearcă peste câteva minute.",
    over: "Seara s-a încheiat", overHint: "Mulțumim că ai fost cu noi. Meniul rămâne aici, dar comenzile s-au închis.",
    from: "de la", until: "până la", adults: "18+",
    offline: "Ești offline. Îți arătăm meniul salvat.",
    footer: "Prețurile includ TVA. Toate sumele în MDL.",
    add: "Adaugă", basket: "Finalizează comanda", qty: "cantitate", less: "Mai puțin", more: "Mai mult",
    soldOut: "S-a terminat",
    unpaid: "Ai o comandă neplătită.",
    unpaidHint: "Plătește-o sau anuleaz-o ca să poți comanda din nou.",
    unpaidGo: "Deschide comanda",
    readyTitle: "Comanda ta e gata!",
    readyHint: "Te așteptăm la bar cu codul",
    readyGo: "Vezi comanda", readyClose: "Am înțeles",
    /*
     * THE ONE WAY iOS HAS: INSTRUCTIONS, DRAWN. Apple gives a page no install
     * prompt to trigger, and — learned the hard way — `navigator.share()`
     * opens the CONTENT-sharing sheet (Messages, Mail, Copy), which does not
     * contain „Adaugă pe ecranul principal" at all; that entry lives only in
     * the browser's own toolbar share menu, which no page can open. So the
     * button opens OUR sheet: two steps, each carrying the exact glyph iOS
     * draws, because "the square with the arrow" is findable and a paraphrase
     * is not. The iOS UI strings are quoted exactly for the same reason.
     */
    install: "Meniul poate sta ca aplicație pe telefonul tău — în Safari sau Chrome.",
    installFooter: "Adaugă meniul ca aplicație pe telefon",
    installGo: "Cum îl adaugi",
    installNo: "Nu acum",
    installTitle: "Meniul, ca aplicație",
    installStep1: "Apasă butonul de trimitere — jos în Safari, sus lângă adresă în Chrome",
    installStep2: "Derulează lista în jos și alege „Adaugă pe ecranul principal”",
    // The entry can be HIDDEN outright until enabled — found on a real phone:
    // some share sheets only reveal it after „Editați acțiunile…”. Without this
    // line, that guest concludes the option does not exist, because it doesn't.
    installMissing: "Nu apare? Apasă „Editați acțiunile…” la capătul listei și activeaz-o.",
    mine: "Comenzile mele", mineClose: "Închide",
    barMode: "Mod bar", barHint: "Comanzi pentru un oaspete. Plata la bar.",
    st_awaiting_payment: "așteaptă plata", st_unpaid_cash_pending: "de plătit la bar",
    st_paid: "în preparare", st_preparing: "în preparare", st_ready: "e gata",
    st_handed_over: "predată", st_failed: "plata nu a trecut",
    st_cancelled: "anulată", st_refunded: "banii returnați",
    // Nu „plata nu a trecut": nimeni n-a încercat nimic, doar a expirat linkul.
    st_expired: "a expirat timpul de plată",
    /*
     * WHO ÎNCASEAZĂ, spelled out at the bottom of the page.
     *
     * „Vânzător" and not „Firma" or „Despre noi": the guest is about to pay
     * somebody, and this says who that somebody is. The IDNO carries no label
     * of its own beyond the acronym, which is the same word in all three
     * languages here because it is what the registry calls it — a translated
     * rendering of a state identifier would be inventing a second one.
     */
    seller: "Vânzător", idno: "IDNO",
    terms: "Termeni și condiții",
  },
  ru: {
    loading: "Загружаем меню…", notFound: "Не находим меню этого события.",
    notFoundHint: "Проверьте, тот ли код вы отсканировали.",
    unpublished: "Меню этого вечера ещё не опубликовано.",
    unpublishedHint: "Код на столе верный. Попробуйте через несколько минут.",
    over: "Вечер завершён", overHint: "Спасибо, что были с нами. Меню остаётся здесь, но заказы закрыты.",
    from: "с", until: "до", adults: "18+",
    offline: "Вы офлайн. Показываем сохранённое меню.",
    footer: "Цены включают НДС. Все суммы в MDL.",
    add: "Добавить", basket: "Оформить заказ", qty: "количество", less: "Меньше", more: "Больше",
    soldOut: "Закончилось",
    unpaid: "У вас есть неоплаченный заказ.",
    unpaidHint: "Оплатите или отмените его, чтобы заказать снова.",
    unpaidGo: "Открыть заказ",
    readyTitle: "Ваш заказ готов!",
    readyHint: "Ждём вас в баре с кодом",
    readyGo: "Открыть заказ", readyClose: "Понятно",
    install: "Меню может жить на вашем телефоне как приложение — в Safari или Chrome.",
    installFooter: "Добавить меню как приложение на телефон",
    installGo: "Как добавить",
    installNo: "Не сейчас",
    installTitle: "Меню как приложение",
    installStep1: "Нажмите кнопку «Поделиться» — внизу в Safari, вверху у адреса в Chrome",
    installStep2: "Прокрутите список вниз и выберите «На экран „Домой“»",
    installMissing: "Не видите? Нажмите «Изменить действия…» в конце списка и включите её.",
    mine: "Мои заказы", mineClose: "Закрыть",
    barMode: "Режим бара", barHint: "Заказ для гостя. Оплата у бара.",
    st_awaiting_payment: "ждём оплату", st_unpaid_cash_pending: "оплата в баре",
    st_paid: "готовится", st_preparing: "готовится", st_ready: "готово",
    st_handed_over: "выдан", st_failed: "оплата не прошла",
    st_cancelled: "отменён", st_refunded: "деньги возвращены",
    st_expired: "время оплаты истекло",
    seller: "Продавец", idno: "IDNO",
    terms: "Условия и положения",
  },
  en: {
    loading: "Loading the menu…", notFound: "We can't find this event's menu.",
    notFoundHint: "Check that you scanned the code on your table.",
    unpublished: "Tonight's menu isn't published yet.",
    unpublishedHint: "The code on your table is right. Try again in a few minutes.",
    over: "The evening has ended", overHint: "Thank you for being with us. The menu stays here, but ordering is closed.",
    from: "from", until: "until", adults: "18+",
    offline: "You're offline. Showing the saved menu.",
    footer: "Prices include VAT. All amounts in MDL.",
    add: "Add", basket: "Complete your order", qty: "quantity", less: "Fewer", more: "More",
    soldOut: "Sold out",
    unpaid: "You have an unpaid order.",
    unpaidHint: "Pay or cancel it to order again.",
    unpaidGo: "Open the order",
    readyTitle: "Your order is ready!",
    readyHint: "Come to the bar with code",
    readyGo: "Open the order", readyClose: "Got it",
    install: "The menu can live on your phone as an app — in Safari or Chrome.",
    installFooter: "Add the menu to your phone as an app",
    installGo: "How to add it",
    installNo: "Not now",
    installTitle: "The menu, as an app",
    installStep1: "Tap the Share button — bottom bar in Safari, top right in Chrome",
    installStep2: "Scroll the list down and choose “Add to Home Screen”",
    installMissing: "Don’t see it? Tap “Edit Actions…” at the end of the list and enable it.",
    mine: "My orders", mineClose: "Close",
    barMode: "Bar mode", barHint: "Ordering for a guest. Paid at the bar.",
    st_awaiting_payment: "waiting for payment", st_unpaid_cash_pending: "pay at the bar",
    st_paid: "being prepared", st_preparing: "being prepared", st_ready: "ready",
    st_handed_over: "handed over", st_failed: "payment didn't go through",
    st_cancelled: "cancelled", st_refunded: "refunded",
    st_expired: "payment window closed",
    seller: "Seller", idno: "IDNO",
    terms: "Terms and conditions",
  },
} as const;

/**
 * The bar goes somewhere. A chevron says so.
 *
 * "Vezi comanda" with a count and a total reads as a SUMMARY — a receipt strip
 * pinned to the bottom of the page — and a guest can sit above it for a while
 * without realising it is the way to the next screen. One mark fixes that, and
 * it is the mark every phone already teaches: a row that leads somewhere has a
 * chevron at its right edge.
 *
 * Deliberately NOT the same as the checkout's mark. That bar carries a banknote
 * on its LEFT, because it takes money and the consequence should be read before
 * the verb; this one carries a chevron on its RIGHT, after the total, because a
 * destination is read last. Different shape (one open stroke against a closed
 * figure), different side, different meaning — which is what stops two bars in
 * the same place on consecutive screens reading as the same button.
 *
 * THE BRIEFS, INCLUDING THE LINE THIS HAS TO ANSWER FOR.
 *
 * Permitted in form: BRIEF-shopping.md:305 — "no icon font — icons must be
 * either inline geometric strokes or nothing." A chevron is two segments and one
 * vertex, which is as close to a hairline as a mark can get while still
 * pointing. BRIEF-dashboard.md:446 names "back chevron, disclosure chevron" as
 * the glyphs that survive its own near-total ban, and the copy deck ships an
 * arrow itself — "← Înapoi la meniu".
 *
 * The line it brushes against is BRIEF-menu.md:317 — "The only glyphs are `−`
 * and `+`. No icon library. If you feel the need for an icon, you are probably
 * about to add a shopping-cart symbol, and you must not." That sentence is about
 * DECORATION AND LABELLING, and its own example gives it away: the thing it
 * forbids is a basket pictogram standing in for the word. This is the opposite —
 * the word stays, in full, in three languages, and the chevron says only which
 * direction it goes. BRIEF-shopping.md:184's "no stepper chevrons" is narrower
 * still: it bans a Cart › Payment › Done breadcrumb, which :319 bans again as a
 * progress bar. Neither is this.
 *
 * If a later reader disagrees, the thing to delete is the chevron, not the
 * full-width tap target underneath it — that was the actual bug.
 */
function GoMark() {
  return (
    <svg
      width="20" height="20" viewBox="0 0 24 24" fill="none"
      aria-hidden="true" focusable="false" className="shrink-0"
    >
      {/* Round caps: white-on-clay is a low-contrast pairing the house accepts
          deliberately (see styles.css), and a mitred point at this weight
          disappears into it. */}
      <path
        d="M9 5l7 7-7 7"
        stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * „Comenzile mele", as a mark rather than a sentence.
 *
 * A DOCKET: the shape of the paper slip a bar hands over, torn along the
 * bottom. It is drawn rather than borrowed — no icon library, per
 * BRIEF-menu.md:317 — and it is four straight strokes and a zigzag, which is
 * the same hairline vocabulary as the chevron on the basket bar.
 *
 * The alternative was three horizontal lines, and it was rejected for reading
 * as a hamburger menu: on a page whose header already holds a navigation
 * strip, a stack of lines promises "more sections", not "your orders".
 *
 * It is never alone. The count sits beside it, and the button carries an
 * `aria-label` with the words, so a screen reader hears „Comenzile mele · 2"
 * rather than a graphic.
 */
function DocketMark() {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      aria-hidden="true" focusable="false" className="shrink-0"
    >
      <path
        d="M6 3h12v17l-2.4-1.6L13.2 20l-2.4-1.6L8.4 20 6 18.4z"
        stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"
      />
      <path
        d="M9.2 8h5.6M9.2 11.6h3.6"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
      />
    </svg>
  );
}

export type AddLine = {
  menu_item_id: string;
  product_id: string;
  name: string;
  price_minor: number;
  upsell_rule_id?: string | null;
};

type MenuProps = {
  slug: string;
  zone: string | null;
  /**
   * MOD BAR — the name of whoever is signed in behind the counter, or null on
   * the ordinary guest path (which is every scan of every QR card).
   *
   * A STRING, INCLUDING THE EMPTY ONE, IS „YES". The empty string is the
   * window where App.tsx has asked /api/auth/me and not yet been answered, and
   * during it this page must already look like the counter's screen — a menu
   * that changes character under somebody's thumb a second after it loads is
   * worse than one that starts as what it is.
   */
  bar: string | null;
  lang: string;
  setLang: (l: string) => void;
  cartCount: number;
  cartSubtotal: number;
  qtyOf: (menuItemId: string) => number;
  onAdd: (line: AddLine) => void;
  onSetQty: (menuItemId: string, qty: number) => void;
  onOpenCart: () => void;
  /** Lifted so the checkout obeys the same house rules this snapshot carries. */
  onRules: (r: Snapshot["rules"]) => void;
  /**
   * The house's own word for where the guest is sitting, lifted for the same
   * reason the rules are: the snapshot is fetched HERE, and the checkout needs
   * what is in it without paying for a second request.
   *
   * Null when the code matches no zone — a card printed against a place since
   * renamed away. The checkout must then say nothing rather than guess.
   */
  onZoneLabel: (label: string | null) => void;
  /**
   * An order from THIS phone, at THIS event, whose money is still an open
   * question (App.tsx found it in localStorage and asked the server). Non-null
   * draws the banner below and turns the basket bar into a door back to the
   * order — pay it or cancel it, then order again.
   */
  unpaidOrderId: string | null;
  /**
   * An order of this guest's that the bar has just finished, found by the
   * watcher in App.tsx. Non-null draws the pop-up over the bottom of the menu
   * — the one thing on this page that is news rather than furniture. Null once
   * the guest dismisses it, or when there is nothing waiting.
   */
  readyOrder: { id: string; code: string } | null;
  onDismissReady: () => void;
  /**
   * Every order this phone placed at this event, newest first. Drawn as
   * „Comenzile mele" in the header — the answer to "what did I order, and
   * what was that code again" once the pop-up has been dismissed.
   */
  myOrders: {
    id: string; code: string; status: string; created_at: number; total_minor?: number;
    /** Failed for want of payment rather than because a bank refused. */
    payment_expired?: boolean;
  }[];
  /**
   * Every item's CURRENT name, reported whenever a snapshot renders — each
   * language is its own snapshot, so the names arrive already translated.
   * The cart uses it to relabel its lines (src/guest/cart.ts), so a basket
   * filled in Romanian does not read Romanian inside a Russian checkout.
   */
  onNames: (names: ReadonlyMap<string, string>) => void;
};

export function Menu({ slug, zone, bar, lang, setLang, cartCount, cartSubtotal, qtyOf, onAdd, onSetQty, onOpenCart, onRules, onZoneLabel, onNames, unpaidOrderId, readyOrder, onDismissReady, myOrders }: MenuProps) {
  const [showOrders, setShowOrders] = useState(false);
  /*
   * THE BASKET TAP THAT HAS TO WAIT — see the bar's onClick.
   *
   * The bar used to answer an unpaid order by NAVIGATING to it, silently: the
   * guest asked for their basket and woke up on an order page they had maybe
   * already read, with nothing saying why. Now the refusal is said in words
   * (the same danger-bordered sheet the checkout uses) and the guest is
   * walked to the clay banner at the top — scrolled there, with the banner
   * wearing the danger border while the sheet is up — to open the unpaid
   * order themselves. Same "sentence + scroll + flag" the checkout's own
   * refusals use, so the product has one way of saying no.
   */
  const [unpaidNudge, setUnpaidNudge] = useState(false);
  const unpaidRef = useRef<HTMLAnchorElement | null>(null);
  /**
   * NOTHING GOES IN THE BASKET WHILE AN ORDER IS UNPAID — owner's call,
   * 2026-08-12, tightening the gate from "the checkout waits" to "the adding
   * waits": „Adaugă", the stepper's + and the basket bar all answer with the
   * refusal sheet and the walk to the banner instead of acting. The MINUS
   * stays free everywhere, for the reason the sold-out stepper keeps its −:
   * the way out of a basket must never be the thing that is locked.
   * Returns true when it refused, so call sites read as one line.
   */
  function refuseWhileUnpaid(): boolean {
    if (!unpaidOrderId) return false;
    setUnpaidNudge(true);
    unpaidRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }
  /*
   * WHETHER THIS BROWSER IS AN iPHONE TAB THAT COULD INSTALL US.
   *
   * The same proxy OrderStatus uses: no Notification API means an iOS tab
   * (Safari or Chrome — both WebKit), the one platform where installing is
   * both possible and invisible, because Apple gives a page no prompt to
   * trigger. NOT shown inside the installed app itself — `display-mode:
   * standalone` (and Safari's own `navigator.standalone`) is the app already
   * installed, where an invitation to install would read as a bug.
   *
   * Dismissed ONCE PER PHONE, not per event: the sentence is about the
   * device, and a guest who said "am înțeles" at one dinner has understood
   * it for the season.
   */
  const installable =
    typeof Notification === "undefined" &&
    !(window.matchMedia?.("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true);
  const [installHintGone, setInstallHintGone] = useState(
    () => localStorage.getItem("prispa.installhint") === "no"
  );
  /** The two-step walkthrough sheet — see the install strip below. */
  const [installOpen, setInstallOpen] = useState(false);
  const [data, setData] = useState<Snapshot | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "unpublished">("loading");
  const [stale, setStale] = useState(false);
  /*
   * WHAT IS STILL POURABLE, AS OF A MOMENT AGO.
   *
   * `null` means WE DO NOT KNOW YET, and it is not the same as "nothing is
   * left". The menu is drawn from a cached snapshot the instant it lands — on a
   * village 4G connection that can be well before the availability call
   * answers, and it is drawn from localStorage when there is no signal at all.
   * Treating an unanswered question as a sold-out menu would grey out a whole
   * evening because one request was slow, which is a far worse lie than the one
   * this feature exists to fix. Nothing is greyed until the room has spoken.
   */
  const [inStock, setInStock] = useState<Set<string> | null>(null);
  const [suggestFor, setSuggestFor] = useState<{ productId: string; name: string } | null>(null);

  /*
   * The basket learns the names this menu is showing — see `onNames` on the
   * props. An effect rather than a render-body call: it writes parent state
   * (the cart), and unlike the palette or the rules it fires on every language
   * switch, not only when the snapshot identity changes.
   */
  useEffect(() => {
    if (!data) return;
    onNames(new Map(data.items.map((i) => [i.id, i.name])));
  }, [data, onNames]);

  /*
   * THE MANIFEST LEARNS WHICH HOUSE THIS IS. On a white-label domain the
   * Worker already knows from the hostname; on the shared domain only the
   * page knows, so once the snapshot lands the <link>s are pointed at the
   * same URLs with `?e=` — and an install taken from this page is named and
   * marked as the pensiune, not the platform. iOS reads the links when the
   * share sheet opens, so a rewrite at load time is early enough.
   */
  useEffect(() => {
    if (!data) return;
    const e = encodeURIComponent(data.event.slug || slug);
    document
      .querySelector<HTMLLinkElement>('link[rel="manifest"]')
      ?.setAttribute("href", `/api/manifest.webmanifest?e=${e}`);
    document
      .querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
      ?.setAttribute("href", `/api/manifest-icon?s=180&e=${e}`);
  }, [data, slug]);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  /*
   * THE CATEGORY STRIP SCROLLS SIDEWAYS AND NOTHING SAID SO.
   *
   * Seven sections do not fit across a phone, the scrollbar is deliberately
   * hidden (`scrollbarWidth: none`), and the only hint that Ceramică exists was
   * a chip clipped by the screen edge — which reads as a layout accident, not
   * as an invitation. A guest who never swipes it never learns the menu has
   * seven parts.
   *
   * Two cues, both earned rather than decorative:
   *   `edges` fades the side that has more to see, and ONLY that side, so the
   *   fade is information: it appears when there is something beyond it and
   *   goes when there is not.
   *   The active chip is kept in view as the page scrolls, so the strip visibly
   *   moves under the guest's thumb without being touched — which teaches that
   *   it moves, and keeps the section they are reading named on screen.
   */
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [edges, setEdges] = useState({ left: false, right: false });
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const t = T[(lang in T ? lang : "ro") as keyof typeof T];

  useEffect(() => {
    if (!slug) {
      setState("missing");
      return;
    }
    const cacheKey = `prispa.menucache.${slug}.${lang}`;
    setState("loading");

    fetch(`/api/menu/${encodeURIComponent(slug)}?lang=${lang}`)
      .then(async (r) => {
        if (!r.ok) {
          /*
           * THE SERVER'S REASON TRAVELS WITH THE FAILURE, and it used not to.
           *
           * `/api/menu/:slug` answers two different 404s: `event_not_found`,
           * where the slug resolves to nothing, and `menu_not_published`, where
           * the evening exists and has simply not been published yet. Both
           * arrived here as `new Error("404")` and were shown as „Nu găsim
           * meniul acestui eveniment. Verifică dacă ai scanat codul de pe masă."
           *
           * That sentence blames the guest for the house's state. The card in
           * their hand is correct; the kitchen has not pressed publish. Sending
           * them to look for another QR code is the one thing that cannot help.
           */
          const body = (await r.json().catch(() => ({}))) as { error?: string };
          throw Object.assign(new Error(String(r.status)), { code: body.error });
        }
        return (await r.json()) as Snapshot;
      })
      .then((snap) => {
        try { localStorage.setItem(cacheKey, JSON.stringify(snap)); } catch { /* quota */ }
        setData(snap);
        setStale(false);
        setState("ready");
      })
      .catch((err: { code?: string }) => {
        /*
         * A SERVER THAT ANSWERED IS AUTHORITATIVE; A NETWORK THAT DID NOT IS NOT.
         *
         * The cached copy exists for the guest standing in a garden on village
         * 4G, and that case is a fetch that never completes — no code. When the
         * server DID answer and said the menu is gone, replaying a cached menu
         * would be showing prices the house has just withdrawn: the whole point
         * of „Retrage publicarea" is that the tables stop seeing it. So the
         * cache is dropped rather than served.
         */
        if (err.code === "menu_not_published") {
          try { localStorage.removeItem(cacheKey); } catch { /* quota */ }
          setState("unpublished");
          return;
        }
        if (err.code === "event_not_found") {
          try { localStorage.removeItem(cacheKey); } catch { /* quota */ }
          setState("missing");
          return;
        }

        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          setData(JSON.parse(cached) as Snapshot);
          setStale(true);
          setState("ready");
        } else {
          setState("missing");
        }
      });
  }, [slug, lang]);

  /*
   * THE ONE THING ON THIS PAGE THAT IS NOT FROZEN.
   *
   * Everything else the guest sees was written into KV at publish and cannot
   * change while they read it — that is the point of the snapshot. Stock is the
   * exception: the last bottle goes at 21.40 and this page has to notice
   * without the guest reloading anything.
   *
   * Thirty seconds, and NOT while the phone is in a pocket. The interval is a
   * compromise between a menu that is wrong for a while and a Durable Object
   * that fifty phones are waking all evening; `document.hidden` is what stops a
   * table of six polling a room nobody is looking at. Coming back to the tab
   * asks immediately, because that is the moment the answer is most likely to
   * have changed and the guest is most likely to act on it.
   *
   * SEPARATE FROM THE SNAPSHOT EFFECT, and deliberately not keyed on `lang`:
   * what is left is the same fact in three languages, and switching language
   * must not restart the clock or blank the greying while a fetch is in flight.
   *
   * A FAILED POLL CHANGES NOTHING. Offline, or a room that will not answer, and
   * the guest keeps whatever was last known — an out-of-date sold-out mark is
   * survivable, and the checkout refuses for real either way. Wiping the set on
   * error would flicker every greyed row back to orderable each time a phone
   * loses signal, which is exactly when it is least able to check again.
   */
  useEffect(() => {
    if (!slug) return;
    let alive = true;

    const poll = async () => {
      if (document.hidden) return;
      try {
        const r = await fetch(`/api/menu/${encodeURIComponent(slug)}/availability`);
        if (!r.ok) return;
        const body = (await r.json()) as { in_stock?: string[] };
        if (!alive || !Array.isArray(body.in_stock)) return;
        setInStock(new Set(body.in_stock));
      } catch {
        /* keep what we last knew */
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), 30_000);
    const onVisible = () => { if (!document.hidden) void poll(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [slug]);

  const grouped = useMemo(() => {
    if (!data) return [];
    return data.categories
      .map((cat) => ({ cat, items: data.items.filter((i) => i.category_id === cat.id) }))
      .filter((g) => g.items.length > 0);
  }, [data]);

  // Highlight whichever category is actually under the nav.
  useEffect(() => {
    if (grouped.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveCat(visible.target.getAttribute("data-cat"));
      },
      { rootMargin: "-150px 0px -60% 0px" }
    );
    for (const el of Object.values(sectionRefs.current)) if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [grouped]);

  // Which sides have more beyond them. Recomputed on scroll, on resize, and
  // whenever the number of chips changes.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      // -1 for sub-pixel widths: at the far right these differ by a fraction
      // and a fade that never quite goes away is worse than no fade.
      setEdges({
        left: el.scrollLeft > 1,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [grouped.length]);

  /*
   * Keep the section being read named on screen.
   *
   * `scrollLeft` by hand rather than `scrollIntoView`, which would also scroll
   * the PAGE vertically to reach the chip — on a sticky bar that means the menu
   * jumping under the guest while they are reading it.
   */
  useEffect(() => {
    const el = scrollerRef.current;
    const chip = activeCat ? chipRefs.current[activeCat] : null;
    if (!el || !chip) return;
    const target = chip.offsetLeft - (el.clientWidth - chip.clientWidth) / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [activeCat]);

  function jumpTo(catId: string) {
    const el = sectionRefs.current[catId];
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
  }

  if (state === "loading") {
    return <div className="flex min-h-dvh items-center justify-center bg-linen px-6"><p className="text-ink-faint">{t.loading}</p></div>;
  }

  if (state === "unpublished") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-linen px-6">
        <div className="max-w-sm text-center">
          <h1 style={{ fontSize: "var(--text-xl)" }}>{t.unpublished}</h1>
          <p className="mt-3 text-ink-muted">{t.unpublishedHint}</p>
        </div>
      </div>
    );
  }

  if (state === "missing" || !data) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-linen px-6">
        <div className="max-w-sm text-center">
          <h1 style={{ fontSize: "var(--text-xl)" }}>{t.notFound}</h1>
          <p className="mt-3 text-ink-muted">{t.notFoundHint}</p>
        </div>
      </div>
    );
  }

  /*
   * THE MENU HAS TO KNOW THE EVENING IS OVER, and it never looked.
   *
   * `Snapshot` has declared `event.status` and `event.ends_at` from the start
   * and read neither. `POST /events/:id/close` deliberately rewrites the KV
   * snapshot precisely so this page can tell — its comment says a menu that
   * offers things while checkout refuses them is worse than a page that says
   * the evening is over — and then the page offered them anyway.
   *
   * The cards are the reason it matters. They sit on tables and in pockets;
   * scanned the next morning they resolve, the menu renders, every Adaugă
   * works, and the refusal arrives only at checkout, after the guest has built
   * a basket.
   *
   * The menu stays readable. It is a nice thing to look at, and the guest may
   * be trying to remember what they drank.
   */
  // The house's colours, painted from the snapshot the guest already has —
  // no extra request on a village 4G connection, and no flash of ours first.
  applyPalette(data.house.palette);
  onRules(data.rules);
  /*
   * The same resolution the header does below, lifted once for the checkout.
   * `?? null` and never the code: a checkout that cannot name the place must
   * fall silent rather than invent a word for it.
   */
  onZoneLabel(zone ? (data.zones?.find((z) => z.code === zone)?.label ?? null) : null);

  const eveningOver =
    data.event.status !== "published" ||
    data.event.ends_at + data.rules.closing_grace_hours * 3600 < Math.floor(Date.now() / 1000);

  return (
    <div className="min-h-dvh bg-linen">
      {/*
        * THE STRIP THAT SAYS THIS IS NOT A GUEST'S PHONE.
        *
        * Above everything, in the house's darkest tone, because the one
        * mistake that costs money here is somebody typing a customer's order
        * into the guest menu they happened to have open — or the reverse, a
        * guest handed a tablet still in mod bar. The two screens are otherwise
        * the same menu on purpose (same prices, same stock, same basket), so
        * the difference has to be stated rather than inferred.
        *
        * The name, when we have it, is the whole point of naming it: the audit
        * row and the order both carry it, and a person is more careful about
        * what has their name on it.
        */}
      {bar !== null && (
        <div className="bg-ink px-5 py-2 text-center text-linen">
          <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-kicker)" }}>
            {t.barMode}{bar ? ` · ${bar}` : ""}
          </p>
          <p className="mt-0.5 opacity-80" style={{ fontSize: "var(--text-kicker)" }}>{t.barHint}</p>
        </div>
      )}
      {eveningOver && (
        <div className="bg-olive-800 px-5 py-3 text-center text-linen">
          <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>{t.over}</p>
          <p className="mt-0.5" style={{ fontSize: "var(--text-kicker)" }}>{t.overHint}</p>
        </div>
      )}
      {/*
        * THE ORDER THAT ISN'T SETTLED YET, before anything else on the page.
        *
        * The whole strip is one link, for the same reason the basket bar is
        * one button: a banner that states a problem and answers on only part
        * of itself teaches the guest that tapping does nothing. Clay, like the
        * basket bar — it is the same kind of object, "the way forward" — and
        * the text arrow is the copy deck's own mark („← Înapoi la meniu"),
        * pointing the other way.
        */}
      {unpaidOrderId && (
        <a
          ref={unpaidRef}
          href={`/o/${encodeURIComponent(unpaidOrderId)}`}
          className="block bg-clay-500 px-5 py-3 text-center text-paper"
          // The danger border while the refusal below points here — the same
          // flag the checkout puts on a field its sentence is about.
          style={unpaidNudge ? { boxShadow: "inset 0 0 0 3px var(--color-danger)" } : undefined}
        >
          <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>{t.unpaid}</p>
          <p className="mt-0.5" style={{ fontSize: "var(--text-kicker)" }}>
            {t.unpaidHint} {t.unpaidGo} →
          </p>
        </a>
      )}
      {/*
        * MOBILE FIRST, AND IT WAS NOT.
        *
        * This was one row: house block on the left, three language buttons on
        * the right, `justify-between`. On a desktop that reads fine. On the
        * 360px phone every guest actually holds, the language nav is a fixed
        * ~140px of the width and the house block gets what is left — so the
        * mark, the house name, the village, the event's name and the place all
        * competed for about 180px. The event name, which is the largest type on
        * the page and the reason the guest scanned anything, wrapped to three
        * lines beside a column of buttons.
        *
        * So it stacks, and the order is the order of the questions a guest
        * actually asks: whose house is this, what is on tonight, and where am I
        * sitting. Language moves onto its own row with the house name, where it
        * is the only other thing competing and can shrink honestly.
        *
        * `sm:` puts it back on one line where there is room for one.
        */}
      <header className="bg-paper px-4 pb-5 pt-5 sm:px-5 sm:pb-6 sm:pt-7">
        <div className="mx-auto max-w-lg">
          {/*
            * THE HOUSE NAMES ITSELF. It used to be the two-doves mark, inlined
            * from twodoveshouse.com — Casa Doi Hulubași's own registered mark,
            * rendered above EVERY subscriber's event. The second guesthouse's
            * guests were greeted by a competitor's logo.
            *
            * A wordmark until there is a per-tenant logo to upload. Set in the
            * display face, so it still reads as a mark and not as a caption.
            */}
          {/* ROW ONE: who the house is, and the language. Both small, both on
              one line, because neither is what the guest came for.
              `gap-4` and not `gap-3`: the kicker truncates, so with too little
              between them a long house name runs right up to the RO button and
              the two read as one object. */}
          <div className="flex min-w-0 items-center gap-4">
            {data.house.logo && (
              /*
               * `?e=` IS NOT A CACHE BUSTER — it is how the route knows whose
               * mark to serve. Bare, this URL only ever resolved on a verified
               * white-labelled hostname, so on the shared origin every house
               * without a custom domain rendered a broken image here. The slug
               * is the one thing this page always has and the guest is already
               * looking at. See worker/api/house.ts.
               */
              <img
                src={`/api/house/logo?e=${encodeURIComponent(data.event.slug || slug)}`}
                alt={data.house.name}
                className="shrink-0"
                style={{ maxWidth: 40, maxHeight: 40, objectFit: "contain" }}
              />
            )}
            {/* WRAPS, does not truncate. The switcher takes a third of a 390px
                phone, so "DOI HULUBAȘI · POHREBEA, DUBĂSARI" became "DOI
                HULUBAȘI · POHR…" — the village cut off mid-word on the line
                whose whole job is to say which house this is. Two short lines
                fit inside the 44px the nav already sets. */}
            <p
              className="min-w-0 flex-1 uppercase text-olive-600"
              style={{ fontSize: "var(--text-kicker)", letterSpacing: "0.1em", fontWeight: 500, lineHeight: 1.35 }}
            >
              {data.house.name}
              {data.house.locality && <span className="text-ink-faint"> · {data.house.locality}</span>}
            </p>

            {/*
              * „COMENZILE MELE" — A MARK ON A ROW THAT ALREADY EXISTS.
              *
              * This was a text link („Comenzile mele · 2") on a line of its
              * own under the event title, and the line was the problem: the
              * header is the part of a phone screen the menu can least afford
              * to spend, and a back-pocket control had taken a whole row of it
              * from the food.
              *
              * Beside the language switcher it costs no vertical space at all
              * — both are 44px tall, both are things a guest reaches for
              * rarely, and they read as one small group of controls rather
              * than two competing announcements. Same square border, same
              * height, so the row stays one object.
              *
              * Still gated on HAVING orders: a guest who has ordered nothing
              * sees nothing, and the header is exactly as it was before.
              */}
            {myOrders.length > 0 && (
              <button
                /*
                  * A TOGGLE, NOT A ONE-WAY DOOR. The same mark that opened the
                  * sheet closes it — a control that only opens leaves the guest
                  * hunting for a different exit than the entrance they used,
                  * and on a phone the thumb is already on this button.
                  */
                onClick={() => setShowOrders((v) => !v)}
                aria-expanded={showOrders}
                aria-label={`${t.mine} · ${myOrders.length}`}
                className="tap flex shrink-0 items-center gap-1.5 border border-dove-400 bg-paper px-2.5 text-ink-muted"
                style={{ borderRadius: 0, minHeight: 44, fontFamily: "var(--font-display)" }}
              >
                <DocketMark />
                <span className="tabular" style={{ fontSize: "var(--text-base)", lineHeight: 1 }}>
                  {myOrders.length}
                </span>
              </button>
            )}

            {/*
              * ON THE SAME LINE AS THE HOUSE, not on a row of its own.
              *
              * Stacked, three 44px-tall buttons became a full-width slab under
              * the title — the loudest object in a header whose job is to say
              * what evening this is. It is a preference most guests never
              * touch. Tight and to the right, it stays reachable (44px is the
              * tap floor and is kept) and stops competing.
              */}
            <nav className="flex shrink-0 border border-dove-400" aria-label="Limbă">
              {LANGS.map(([code, label]) => (
                <button
                  key={code} onClick={() => setLang(code)} aria-pressed={lang === code}
                  className={`px-2.5 ${lang === code ? "bg-clay-500 text-paper" : "bg-paper text-ink-muted"}`}
                  style={{ borderRadius: 0, fontSize: "var(--text-kicker)", minHeight: 44, minWidth: 40 }}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {/* ROW TWO: the thing the guest scanned the card to find out. Full
              width, nothing beside it — and far enough below the language
              buttons that the title is not read as a caption to them. */}
          <h1 className="mt-5 text-balance" style={{ fontSize: "var(--text-xl)" }}>
            {data.event.name}
          </h1>

          {/* „Comenzile mele" now lives on the row above, as a docket and a
              number — see the note there. It is the answer to "what did I
              order, and what was that code again" once „Am înțeles" has
              dismissed the ready pop-up for good. */}
        </div>

        {/*
          * THE PLACE IS NO LONGER ANNOUNCED HERE. Deliberate, and reversible.
          *
          * The header carried a line naming the zone — „Ești la Pergola 3" —
          * and the preposition was the problem: it has to fit every kind of
          * place a house can have, and no one set of words does. „Ești la Foc
          * de tabără" is already a stretch, and the Russian was worse than
          * ambiguous — «Вы за» is an unfinished sentence that only completes
          * with the word for the furniture we deliberately stopped assuming
          * (за столом). Dropping the preposition and leaving a bare place name
          * under the event title was the other option, and it reads as a
          * subtitle to the event rather than as an answer to "where am I".
          *
          * So it is not shown for now. NOTHING BEHIND IT WAS REMOVED: `zones`
          * still travels in the snapshot, and the label is still lifted to the
          * checkout below, where it pre-fills the guest's name and is the one
          * place it demonstrably earns its keep — that string becomes
          * `orders.guest_name` and is what the bar shouts. Putting the line
          * back is a matter of rendering `zone` again, once there is wording
          * that survives all three languages and every kind of place.
          */}

        {stale && (
          <p className="mx-auto mt-3 max-w-lg border border-dove-200 bg-mist px-3 py-2 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
            {t.offline}
          </p>
        )}
      </header>

      {/*
        * THE INSTALL HINT, in the flow rather than fixed — it scrolls away
        * with the header instead of competing with the basket bar for the
        * bottom of the screen, and „Am înțeles" retires it for good on this
        * phone. See `installable` above for who gets it.
        */}
      {installable && !installHintGone && (
        <div className="border-b border-dove-200 bg-paper px-5 py-3">
          <div className="mx-auto max-w-lg">
            <p className="text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
              {t.install}
            </p>
            <div className="mt-2 flex items-center gap-2">
              {/*
                * NOT `navigator.share()` — that was tried, and the sheet it
                * opens (Messages, Mail, Copy) does not contain „Adaugă pe
                * ecranul principal" at all; see install-marks.tsx. The button
                * opens OUR walkthrough: two steps, each wearing the exact
                * glyph the guest must find, and its label says exactly what
                * it does.
                */}
              <button
                onClick={() => setInstallOpen(true)}
                className="tap flex-1 border border-clay-500 px-4 text-clay-700"
                style={{ borderRadius: 0, minHeight: 44, fontFamily: "var(--font-display)" }}
              >
                {t.installGo}
              </button>
              <button
                onClick={() => {
                  setInstallHintGone(true);
                  try {
                    localStorage.setItem("prispa.installhint", "no");
                  } catch {
                    /* quota — worst case the hint returns next visit */
                  }
                }}
                className="tap shrink-0 px-4 text-ink-faint"
                style={{ borderRadius: 0, minHeight: 44 }}
              >
                {t.installNo}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category navigation. The menu is drinks-led, so Bere must be one tap
          away from anywhere. Horizontal and thumb-height, not a dropdown. */}
      <nav className="sticky top-0 z-30 border-y border-dove-200 bg-paper" aria-label="Categorii">
        {/*
          * A SOFT EDGE UNDER THE STICKY BAR.
          *
          * The bar is opaque and the menu scrolls beneath it, so a row was
          * guillotined at the hairline: half a bottle, the top of an "Adaugă"
          * button, a price cut through the middle. At a glance it reads as
          * broken layout rather than as content continuing underneath — the one
          * thing a guest scrolling a menu should never have to work out.
          *
          * A short strip below the bar that blurs what passes under it and
          * fades to nothing. The MASK is what makes it work: without it the
          * blur would end on its own hard line and we would have traded one
          * edge for another. `top-full` hangs it off the bar's bottom, outside
          * the paper, so the bar itself stays crisp.
          *
          * pointer-events-none, or it would swallow taps on the first row.
          */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-full h-7"
          style={{
            backdropFilter: "blur(9px)",
            WebkitBackdropFilter: "blur(9px)",
            background: "linear-gradient(to bottom, var(--color-linen), transparent)",
            maskImage: "linear-gradient(to bottom, #000 10%, transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, #000 10%, transparent)",
          }}
        />
        <div className="relative mx-auto max-w-lg">
        <div
          ref={scrollerRef}
          className="relative flex gap-2 overflow-x-auto px-5 py-2"
          style={{ scrollbarWidth: "none" }}
        >
          {grouped.map(({ cat }) => (
            <button
              key={cat.id}
              ref={(el) => { chipRefs.current[cat.id] = el; }}
              onClick={() => jumpTo(cat.id)}
              aria-current={activeCat === cat.id ? "true" : undefined}
              className={`flex shrink-0 items-center gap-2 whitespace-nowrap pr-4 ${
                activeCat === cat.id ? "bg-clay-500 text-paper" : "border border-dove-400 bg-paper text-ink-muted"
              }`}
              style={{ borderRadius: 0, minHeight: 44, fontFamily: "var(--font-display)" }}
            >
              {/*
                * ALWAYS, PHOTOGRAPH OR DRAWING — the same rule the product rows
                * have always followed.
                *
                * This was gated on `cat.photo`, so a section with no photograph
                * was a bare word while the products inside it all carried a
                * picture. The argument for the gate was that a repeated line
                * drawing is wallpaper, and that was true of the 120px band this
                * replaced. At 44px it is furniture: the row holds its shape
                * whatever is in it, and /api/img/category/:id already answers
                * with the drawing chosen for THIS section — a bottle for Bere,
                * a flute for Spumant — so the chips stay tellable apart.
                *
                * Flush to the chip's left edge, full height, no gap — it reads
                * as part of the button rather than an icon sitting on it.
                */}
              <img
                src={`/api/img/category/${cat.id}`}
                alt=""
                loading="lazy"
                className="block shrink-0 self-stretch"
                style={{ width: 44, objectFit: "cover" }}
              />
              {cat.name}
            </button>
          ))}
        </div>

        {/*
          * THE SIDE THAT HAS MORE, and only that side.
          *
          * Paper fading to nothing over the last few millimetres, so a chip is
          * dissolving rather than clipped by the screen — clipped reads as a
          * bug, dissolving reads as "keep going". They are rendered only when
          * there is genuinely something past the edge, which is what makes them
          * information instead of ornament: on a house with three sections that
          * fit, neither ever appears.
          */}
        {edges.left && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-10"
            style={{ background: "linear-gradient(to right, var(--color-paper), transparent)" }}
          />
        )}
        {edges.right && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-10"
            style={{ background: "linear-gradient(to left, var(--color-paper), transparent)" }}
          />
        )}
        </div>
      </nav>

      <main className="mx-auto max-w-lg px-5 pb-32">
        {grouped.map(({ cat, items }) => (
          <section
            key={cat.id}
            data-cat={cat.id}
            ref={(el) => { sectionRefs.current[cat.id] = el; }}
            className="mt-8"
          >
            {/*
              * THE SECTION'S OWN PICTURE — A SQUARE, NOT A BAND.
              *
              * It was a 120px full-width band, and that was wrong twice. The
              * generated placeholders are SQUARE with a tall centred object
              * (flux-1-schnell takes no width or height, so square is all there
              * is), and `cover` into a 120px band crops them to a horizontal
              * stripe through the middle of the bottle. And on a phone a band
              * per section pushes the actual menu down the page — the opposite
              * of what a mobile-first header is for.
              *
              * A square crops cleanly whatever it is given: no crop at all for
              * the square drawings, a centre crop for a real photograph. It
              * also matches the nav chip above and the product rows below, so
              * the page has one size of picture instead of three.
              *
              * Drawn ALWAYS, photograph or drawing, exactly as the product
              * rows are. /api/img/category/:id answers with the section's own
              * line drawing when nobody has photographed it yet, which is the
              * same stand-in the products inside it are already showing — a
              * heading that stayed a bare word while its own products all
              * carried a picture just looked unfinished.
              */}
            <div className="flex items-center gap-3 border-b border-dove-200 pb-2">
              <img
                src={`/api/img/category/${cat.id}`}
                alt=""
                width={56}
                height={56}
                loading="lazy"
                className="shrink-0 border border-dove-200 bg-paper"
                style={{ width: 56, height: 56, objectFit: "cover" }}
              />
              <h2 className="min-w-0" style={{ fontSize: "var(--text-xl)" }}>{cat.name}</h2>
            </div>

            <ul className="m-0 list-none p-0">
              {items.map((item) => {
                const qty = qtyOf(item.id);
                /*
                 * `inStock !== null` FIRST, ALWAYS. Before the room has
                 * answered, the set is unknown and nothing is finished — see
                 * the state's own note. Once it has, absence IS the answer: the
                 * room lists what it could reserve, so an id that is not there
                 * cannot be ordered, whether it ran out or was never loaded.
                 */
                const finished = inStock !== null && !inStock.has(item.id);
                return (
                  <li
                    key={item.id}
                    className="flex gap-4 border-b border-dove-100 py-4 last:border-b-0"
                    /*
                     * DIMMED, NOT REMOVED. BRIEF-menu.md §3.2: "not removed
                     * from the menu (guests ask 'do you have…?' and an absent
                     * row cannot answer)". The row stays readable — it is how a
                     * guest finds out the house makes the thing at all, and how
                     * they remember what they drank last time. It simply stops
                     * looking like something they can have.
                     *
                     * A quiet opacity and nothing else: no strikethrough, no
                     * red, no badge. §14 forbids all three, and the house has
                     * no alarm colours.
                     */
                    style={finished ? { opacity: 0.55 } : undefined}
                  >
                    {/* The picture is the fastest way to know what this is —
                        especially for a guest reading a language they don't. */}
                    <img
                      src={`/api/img/${item.product_id}`}
                      alt=""
                      width={72}
                      height={72}
                      loading="lazy"
                      className="shrink-0 border border-dove-200 bg-paper"
                      style={{ width: 72, height: 72, objectFit: "cover" }}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 style={{ fontSize: "var(--text-base)" }}>{item.name}</h3>
                        <span className="tabular shrink-0" style={{ fontSize: "var(--text-base)" }}>{lei(item.price_minor)}</span>
                      </div>

                      {item.producer && <p className="text-olive-600" style={{ fontSize: "var(--text-kicker)" }}>{item.producer}</p>}
                      {item.story && <p className="mt-0.5 italic text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>{item.story}</p>}
                      {item.description && <p className="mt-0.5 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>{item.description}</p>}

                      <div className="mt-2 flex items-center justify-between gap-3">
                        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                          {item.age_restricted && <span className="border border-dove-400 px-1.5">{t.adults}</span>}
                          {item.available_from && <span>{t.from} {chisinauTime(item.available_from)}</span>}
                          {item.available_until && <span>{t.until} {chisinauTime(item.available_until)}</span>}
                          {/* SAID ONCE. When the row still has a stepper the
                              sentence belongs here, beside the other facts
                              about the item, because the right-hand slot is
                              holding the only way left to take it back out of
                              the order. When there is no stepper it moves into
                              that slot instead — where the guest is already
                              looking for the answer to "can I have this?" —
                              and never appears in both places. */}
                          {finished && qty > 0 && <span>{t.soldOut}</span>}
                        </p>

                        {eveningOver ? null : finished && qty === 0 ? (
                          /*
                           * WHERE "ADAUGĂ" WAS. §14: "When something is
                           * finished, say it is finished, once, quietly." The
                           * eye goes to this corner of the row to order, so
                           * this is the corner that has to answer — and a
                           * sentence in the ink of a caption answers it without
                           * the guest having to work out why a button is grey.
                           *
                           * NO TIME OF DAY IN THE WORDS. This first read
                           * „S-a terminat pentru astă-seară", borrowing the
                           * brief's own name for the variant ("Finished for
                           * tonight", §3.2). But an event is not always an
                           * evening. `archetype` has held `workshop` and
                           * `retreat` since migration 0003 and `festival` since
                           * 0020, `starts_at` is whatever hour the manager
                           * types, and a guest at a Saturday-morning workshop
                           * told that something is finished FOR TONIGHT is
                           * being told about a different day than the one they
                           * are standing in — which sounds like the house has
                           * confused their event with somebody else's.
                           *
                           * The bare verb is true at every hour. It is also
                           * shorter, which is what this slot wanted anyway.
                           *
                           * NOT a disabled button. A greyed-out control invites
                           * the tap that proves it is dead, and BRIEF-shopping
                           * §126 is explicit that an inert control must be
                           * "explained, never a silently greyed button".
                           */
                          <p
                            className="shrink-0 text-right text-ink-faint"
                            style={{
                              fontFamily: "var(--font-display)",
                              fontSize: "var(--text-kicker)",
                              maxWidth: "9rem",
                              lineHeight: 1.3,
                            }}
                          >
                            {t.soldOut}
                          </p>
                        ) : qty === 0 ? (
                          <button
                            onClick={() => {
                              if (refuseWhileUnpaid()) return;
                              onAdd({
                                menu_item_id: item.id,
                                product_id: item.product_id,
                                name: item.name,
                                price_minor: item.price_minor,
                              });
                              // The pairing is offered ONCE, on the first add.
                              // Showing it again on every +1 would nag.
                              setSuggestFor({ productId: item.product_id, name: item.name });
                            }}
                            className="shrink-0 border border-clay-500 px-5 text-clay-700"
                            style={{ borderRadius: 0, minHeight: 44, fontFamily: "var(--font-display)" }}
                            aria-label={`${t.add} — ${item.name}`}
                          >
                            {t.add}
                          </button>
                        ) : (
                          /* Once it is in the order the button becomes a stepper,
                             so changing your mind costs one tap and never sends
                             you to another screen. */
                          <div
                            className="flex shrink-0 items-center border border-olive-500"
                            role="group"
                            aria-label={`${item.name} — ${t.qty}`}
                          >
                            <button
                              onClick={() => onSetQty(item.id, qty - 1)}
                              aria-label={`${t.less} ${item.name}`}
                              className="text-olive-600"
                              style={{ minHeight: 44, minWidth: 44, borderRadius: 0, fontSize: "var(--text-lg)" }}
                            >
                              −
                            </button>
                            <span
                              className="tabular text-center"
                              aria-live="polite"
                              style={{ minWidth: 34, fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}
                            >
                              {qty}
                            </span>
                            {/*
                              * NO "+" ON SOMETHING THAT IS FINISHED, and the
                              * "−" stays.
                              *
                              * This is the row a guest reaches when the last
                              * one went while their basket was open. Removing
                              * the whole stepper would strand what they already
                              * hold: the item sits in the cart, the checkout
                              * refuses the order because of it, and the menu —
                              * the only place quantities can be changed — has
                              * nothing left to tap. So the way OUT survives and
                              * only the way further in is gone.
                              *
                              * Dropped rather than disabled, for the reason the
                              * sentence above gives: a dead grey button asks to
                              * be tapped.
                              */}
                            {!finished && (
                              <button
                                onClick={() => { if (refuseWhileUnpaid()) return; onSetQty(item.id, qty + 1); }}
                                aria-label={`${t.more} ${item.name}`}
                                className="bg-olive-500 text-paper"
                                style={{ minHeight: 44, minWidth: 44, borderRadius: 0, fontSize: "var(--text-lg)" }}
                              >
                                +
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        <p className="mt-10 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>{t.footer}</p>
        {/*
          * THE HOUSE'S OWN LAST WORD — whichever house this is.
          *
          * This line was „Încetinim și meșterim.", hardcoded: Casa Doi
          * Hulubași's tagline, printed at the bottom of EVERY subscriber's
          * menu. Exactly the two-doves mark's bug in another costume, and it
          * outlived that fix because a sentence in faint 12px type does not
          * look like branding until you are the second guesthouse reading a
          * competitor's motto under your own food.
          *
          * It comes from the snapshot now (tenant setting `menu_footer_note`),
          * and a house that has written nothing gets NOTHING — no placeholder,
          * no borrowed voice, no empty <p> holding a line of space open.
          */}
        {data.house.footer_note && (
          <p className="mt-1 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
            {data.house.footer_note}
          </p>
        )}

        {/*
          * WHO IS SELLING, AND UNDER WHAT TERMS.
          *
          * The acquiring bank verifies before it signs an integration that the
          * seller is named on the site the money is taken from — the
          * registered company, its IDNO, its registered address — and that its
          * terms are one tap away. This is that block, and every line of it is
          * TENANT DATA out of the snapshot: nothing here is compiled in, for
          * exactly the reason the motto above it stopped being compiled in.
          *
          * RENDERS NOTHING WHEN THERE IS NOTHING. `house.legal` is absent
          * rather than blank for a house that has filled none of it in, each
          * of the three lines is drawn only if it has content, and the terms
          * link only exists where an address was typed. No heading over an
          * empty box, no „IDNO" with a dash after it, and no link to a page
          * nobody wrote.
          *
          * Faint 12px under the VAT line, because it is a legal necessity
          * rather than something anybody came here to read — the same weight
          * a printed menu gives the same words.
          */}
        {(data.house.legal || data.rules.terms_url) && (
          <div className="mt-6 border-t border-dove-100 pt-4">
            {data.house.legal && (
              <>
                <p className="uppercase text-ink-faint" style={{ fontSize: "var(--text-kicker)", letterSpacing: "0.1em" }}>
                  {t.seller}
                </p>
                {data.house.legal.name && (
                  <p className="mt-1 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
                    {data.house.legal.name}
                  </p>
                )}
                {data.house.legal.idno && (
                  <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                    {t.idno} <span className="tabular">{data.house.legal.idno}</span>
                  </p>
                )}
                {data.house.legal.address && (
                  <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                    {data.house.legal.address}
                  </p>
                )}
              </>
            )}
            {data.rules.terms_url && (
              <p className={data.house.legal ? "mt-2" : ""}>
                <a
                  href={data.rules.terms_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-clay-600 underline"
                  style={{ fontSize: "var(--text-kicker)" }}
                >
                  {t.terms}
                </a>
              </p>
            )}
          </div>
        )}

        {/*
          * THE INSTALL, FINDABLE FOREVER. The header strip is dismissed once
          * per phone, and „Nu acum" must mean it — but a guest who changes
          * their mind next weekend needs somewhere to look, and the footer is
          * where a menu keeps the things it says quietly. Same walkthrough
          * sheet, always on offer, only where installing is possible at all.
          */}
        {installable && (
          <p className="mt-6">
            <button
              onClick={() => setInstallOpen(true)}
              className="text-clay-600 underline"
              style={{ borderRadius: 0, fontSize: "var(--text-kicker)", minHeight: 44 }}
            >
              {t.installFooter}
            </button>
          </p>
        )}
      </main>

      {suggestFor && (
        <Suggestion
          eventSlug={slug}
          productId={suggestFor.productId}
          afterName={suggestFor.name}
          lang={lang}
          onAdd={onAdd}
          onClose={() => setSuggestFor(null)}
        />
      )}

      {/*
        * THE EVENING'S ORDERS, FROM THIS PHONE.
        *
        * Same bottom sheet as the pairing suggestion and the ready pop-up, so
        * the page has one kind of overlay rather than three. Each row is the
        * whole tap target and leads to /o/<id>, where the code is enormous and
        * the receipt is itemised — this list is an index, not a replacement.
        *
        * THE TOTAL IS SHOWN ONLY WHERE WE STILL HOLD THE SESSION. A phone that
        * has lost `prispa.order.<id>` gets the limited response from the
        * server (no money in it at all), and rendering "0 lei" for that would
        * be a lie about somebody's bill. `undefined` simply draws nothing.
        */}
      {showOrders && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 max-h-[80dvh] overflow-y-auto border-t border-dove-200 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.15)]"
          style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="mx-auto max-w-lg">
            {/*
              * „ÎNCHIDE" AT THE TOP RIGHT, WHERE A SHEET IS CLOSED. It was a
              * full-width strip under the list, which on a long evening sat
              * below the fold of the sheet's own scroll — the exit was the one
              * thing that moved. The title row is always visible; the corner
              * of it is where every other sheet on a phone puts the way out.
              */}
            <div className="flex items-center justify-between gap-4">
              <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>{t.mine}</p>
              <button
                onClick={() => setShowOrders(false)}
                className="tap -mr-2 shrink-0 px-3 text-ink-faint"
                style={{ borderRadius: 0, minHeight: 44 }}
              >
                {t.mineClose}
              </button>
            </div>

            <ul className="m-0 mt-3 list-none p-0">
              {myOrders.map((o) => (
                <li key={o.id} className="border-t border-dove-100 first:border-t-0">
                  <a
                    href={`/o/${encodeURIComponent(o.id)}`}
                    className="flex items-center gap-4 py-3"
                    style={{ minHeight: 44 }}
                  >
                    <span
                      className="tabular shrink-0"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: "var(--text-xl)",
                        lineHeight: 1,
                        color: o.status === "ready" ? "var(--color-olive-500)" : "var(--color-ink)",
                      }}
                    >
                      {o.code}
                    </span>
                    <span className="min-w-0 flex-1">
                      {/* The status word wears its state's color (status-tone.ts)
                          — the same tone the order page's sentence uses, so the
                          list and the page never disagree about the same fact.
                          Weight 500 because a color alone drowns at this size. */}
                      <span
                        className="block"
                        style={{ color: statusTone(o.status), fontWeight: 500 }}
                      >
                        {/* „a expirat timpul de plată", not „plata nu a trecut" —
                            this list is the only way back into an expired order,
                            so it must not be the one place that still blames a
                            card nobody ever charged. */}
                        {(t as Record<string, string>)[
                          o.payment_expired ? "st_expired" : `st_${o.status}`
                        ] ?? o.status}
                      </span>
                      {o.created_at > 0 && (
                        <span className="block text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                          {chisinauTime(o.created_at)}
                        </span>
                      )}
                    </span>
                    {o.total_minor !== undefined && (
                      <span className="tabular shrink-0">{lei(o.total_minor)}</span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/*
        * THE POP-UP. The only thing on this page that is news.
        *
        * Same anchor and the same z-40 as the pairing suggestion above, for
        * the same reason: the bottom of a phone is where a thumb already is,
        * and one bottom object at a time is the rule this page follows (the
        * basket bar below hides while either is up). Olive, because olive is
        * what „gata" means everywhere else in the product — the order page
        * turns the pickup code olive at the same moment.
        *
        * The CODE is the largest thing in it. That is the whole errand: the
        * guest stands up, walks to the bar, and says it. The sound and the
        * vibration have already happened (src/guest/ready-alert.ts); this is
        * what is left on screen once they look.
        *
        * Dismissible, and dismissal is honest — „Am înțeles" removes it for
        * this visit, not for ever, so a reload while the drink is still on
        * the counter puts the code back.
        */}
      {readyOrder && !showOrders && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-olive-500 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.25)]"
          style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="mx-auto flex max-w-lg items-center gap-4">
            <div className="min-w-0 flex-1">
              <p
                className="text-olive-600"
                style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-xl)" }}
              >
                {t.readyTitle}
              </p>
              <p className="mt-1 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
                {t.readyHint}
              </p>
            </div>
            <span
              className="tabular shrink-0 text-olive-600"
              style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-3xl)", lineHeight: 1 }}
            >
              {readyOrder.code}
            </span>
          </div>

          <div className="mx-auto mt-4 grid max-w-lg gap-2">
            <a
              href={`/o/${encodeURIComponent(readyOrder.id)}`}
              className="tap flex items-center justify-center bg-olive-500 px-6 text-paper"
              style={{ borderRadius: 0, fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}
            >
              {t.readyGo}
            </a>
            <button
              onClick={onDismissReady}
              className="tap w-full px-6 text-ink-faint"
              style={{ borderRadius: 0 }}
            >
              {t.readyClose}
            </button>
          </div>
        </div>
      )}

      {/*
        * THE INSTALL WALKTHROUGH — two steps, each wearing the exact glyph
        * the guest has to find on their own toolbar. Instructions are all a
        * page can honestly offer here (see install-marks.tsx for the whole
        * story), so they are the best instructions we can draw.
        */}
      {installOpen && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-dove-200 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.15)]"
          style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="mx-auto max-w-lg">
            <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>
              {t.installTitle}
            </p>
            <div className="mt-4 flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-dove-300 text-ink">
                <ShareMark />
              </span>
              <p className="text-ink-muted">1 · {t.installStep1}</p>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-dove-300 text-ink">
                <AddHomeMark />
              </span>
              <p className="text-ink-muted">2 · {t.installStep2}</p>
            </div>
            <p className="mt-3 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
              {t.installMissing}
            </p>
            <button
              onClick={() => setInstallOpen(false)}
              className="tap mt-4 w-full border border-dove-400 bg-paper px-6 text-ink-muted"
              style={{ borderRadius: 0, fontFamily: "var(--font-display)" }}
            >
              {t.readyClose}
            </button>
          </div>
        </div>
      )}

      {/*
        * THE REFUSAL THE BAR GIVES WHILE AN ORDER IS UNPAID — the same
        * danger-bordered sheet the checkout's own refusals use. „Am înțeles"
        * closes it; „Deschide comanda" is the way through, and it is the same
        * destination the flagged banner at the top offers, said twice on
        * purpose: once where the guest tapped, once where the fact lives.
        */}
      {unpaidNudge && unpaidOrderId && (
        <div
          role="alert"
          className="fixed inset-x-0 bottom-0 z-40 border-t-2 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.25)]"
          style={{
            borderTopColor: "var(--color-danger)",
            paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <div className="mx-auto max-w-lg">
            <p style={{ color: "var(--color-danger)" }}>{t.unpaid}</p>
            <p className="mt-1 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>{t.unpaidHint}</p>
            <div className="mt-4 grid gap-2">
              <a
                href={`/o/${encodeURIComponent(unpaidOrderId)}`}
                className="tap flex items-center justify-center bg-clay-500 px-6 text-paper"
                style={{ borderRadius: 0, fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}
              >
                {t.unpaidGo}
              </a>
              <button
                onClick={() => setUnpaidNudge(false)}
                className="tap w-full px-6 text-ink-faint"
                style={{ borderRadius: 0 }}
              >
                {t.readyClose}
              </button>
            </div>
          </div>
        </div>
      )}

      {cartCount > 0 && !suggestFor && !readyOrder && !showOrders && !eveningOver && !unpaidNudge && !installOpen && (
        /*
          * THE WHOLE STRIP IS THE BUTTON.
          *
          * It used to be a <button> inside a paper <div> with p-3, so twelve
          * pixels of clay-coloured-looking bar on every side — and on a wide
          * screen everything outside max-w-lg — swallowed the tap and did
          * nothing. A bar that looks like one object and answers on only part
          * of itself is the worst kind of control: the guest taps, nothing
          * happens, and there is no way to tell that they missed.
          *
          * The paper wrapper and its border are gone with it: clay against
          * linen already separates the bar from the menu, and the border was
          * drawing a line around a thing that is no longer a box inside a box.
          */
        <button
          /*
            * AN UNPAID ORDER STOPS THE BAR IN WORDS, NOT WITH A TELEPORT. It
            * used to navigate straight to /o/<id>: the guest asked for their
            * basket and woke up on an order page with nothing saying why.
            * Now the refusal is the sheet above, and the banner that answers
            * it is scrolled into view wearing the flag.
            */
          onClick={() => { if (refuseWhileUnpaid()) return; onOpenCart(); }}
          /* The count is drawn, so it must also be SAID. One name for the
             whole button, rather than a screen reader reading a bare numeral
             and then the words as if they were unrelated. */
          aria-label={`${t.basket} — ${cartCount} · ${lei(cartSubtotal)}`}
          className="fixed inset-x-0 bottom-0 z-30 flex w-full items-center justify-center bg-clay-500 px-4 text-paper"
          style={{
            borderRadius: 0,
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-lg)",
            minHeight: 64,
            paddingTop: 12,
            /*
             * THE HOME INDICATOR WAS SITTING ON THIS BUTTON.
             *
             * index.html sets `viewport-fit=cover`, which extends the page
             * under the iPhone's rounded corners and home indicator — and
             * nothing in the app has ever read `env(safe-area-inset-bottom)`.
             * So on every iPhone since the X, the bottom ~34px of this bar lay
             * beneath the indicator: half the button unreachable, and a swipe
             * aimed at it going to the system instead. The fallback 0px keeps
             * Android and desktop exactly as they were.
             */
            paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <span className="flex w-full max-w-lg items-center justify-between gap-4">
            <span className="flex min-w-0 items-center gap-3">
              {/*
                * THE ONE CIRCLE IN A SQUARE-CORNERED HOUSE.
                *
                * Everything here is `borderRadius: 0` on purpose — hairlines and
                * flat surfaces, no rounded chrome. The count is the deliberate
                * exception, and it earns it: a counter has to read as a counter
                * at a glance, from a phone held at arm's length in a courtyard
                * at dusk, and a circle is the only shape that does that without
                * a label. It was "Vezi comanda · 3", where the number arrived
                * after the sentence and looked like part of it.
                */}
              {/*
                * `text-ink`, NOT `text-clay-700`.
                *
                * applyPalette() above rewrites --color-clay-700 to whichever
                * accentDeep this house chose, so the numeral's contrast is a
                * per-tenant accident rather than a decision. On `lut` — the
                * default every house has until it picks another — clay-700 on
                * paper measures 4.36:1, under AA for 17px text, on a screen the
                * brief says is read outdoors at arm's length. --color-ink is
                * the one ink the palette does not touch: 17:1 everywhere.
                */}
              <span
                aria-hidden
                className="flex shrink-0 items-center justify-center bg-paper text-ink"
                style={{
                  // `minWidth`, not `width`: one and two digits are narrower
                  // than 34px so the disc stays a true circle, and a table of
                  // twelve that somehow reaches three grows into a lozenge
                  // rather than spilling its numeral outside the shape.
                  borderRadius: 999,
                  minWidth: 34, height: 34, padding: "0 9px",
                  fontSize: "var(--text-base)", lineHeight: 1,
                  fontVariantNumeric: "tabular-nums lining-nums",
                }}
              >
                {cartCount}
              </span>
              <span className="truncate">{t.basket}</span>
            </span>
            <span className="flex shrink-0 items-center gap-3">
              <span className="tabular">{lei(cartSubtotal)}</span>
              <GoMark />
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
